import { useMemo } from "react";
import { Activity } from "lucide-react";
import { BaseChart } from "../shared/BaseChart";
import type { EChartsOption } from "echarts";
import type { AIAnalysis } from "../../core";
import { getChartTheme } from "../../utils/chartTheme";
import { useTheme } from "../../context/ThemeContext";

interface HistoricalRiskChartProps {
	riskHistory: AIAnalysis[];
	loading: boolean;
}

export default function HistoricalRiskChart({ riskHistory, loading }: HistoricalRiskChartProps) {
	const { resolvedTheme } = useTheme();

	const chartOption = useMemo((): EChartsOption => {
		if (!riskHistory || riskHistory.length === 0) return {};

		const chartTheme = getChartTheme(resolvedTheme);
		// gateway orders newest-first; charts read left-to-right chronologically
		const orderedHistory = [...riskHistory].reverse();
		const dates = orderedHistory.map((entry) => entry.date);
		const riskScores = orderedHistory.map((entry) => entry.risk_score);

		return {
			animation: true,
			backgroundColor: "transparent",
			grid: {
				top: 32,
				right: 16,
				left: 8,
				bottom: 32,
				outerBoundsMode: "same",
			},
			tooltip: {
				trigger: "axis",
				backgroundColor: chartTheme.tooltipBg,
				borderColor: chartTheme.tooltipBorder,
				textStyle: { color: chartTheme.tooltipText },
				valueFormatter: (value) => (typeof value === "number" ? value.toFixed(1) : "N/A"),
			},
			xAxis: {
				type: "category",
				boundaryGap: false,
				data: dates,
				axisLabel: { color: chartTheme.axisLabel },
				axisLine: { lineStyle: { color: chartTheme.axisLine } },
			},
			yAxis: {
				type: "value",
				min: 0,
				max: 10,
				axisLabel: { color: chartTheme.axisLabelSecondary },
				splitLine: { lineStyle: { color: chartTheme.splitLine, type: "dashed" } },
			},
			series: [
				{
					name: "Risk Score",
					type: "line",
					data: riskScores,
					showSymbol: riskScores.length <= 1,
					connectNulls: true,
					itemStyle: { color: "#3b82f6" },
					lineStyle: { color: "#3b82f6", width: 2 },
					areaStyle: { color: "#3b82f6", opacity: 0.1 },
				},
			],
		};
	}, [riskHistory, resolvedTheme]);

	if (loading) {
		return <div className="h-56 rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 animate-pulse" />;
	}

	if (!riskHistory || riskHistory.length === 0) {
		return (
			<div className="flex h-56 flex-col items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 p-6 shadow-md">
				<Activity className="text-zinc-400 dark:text-zinc-600" size={28} />
				<p className="text-sm text-zinc-500">No historical risk data available yet</p>
			</div>
		);
	}

	return (
		<div className="flex w-full flex-col gap-4 rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 p-6 shadow-md">
			<div>
				<h2 className="text-xl font-bold tracking-tight text-zinc-950 dark:text-white">Historical Risk Score</h2>
				<p className="text-sm text-zinc-600 dark:text-zinc-400">Track how the AI-assessed risk score has evolved over time.</p>
			</div>
			<div className="h-48 w-full">
				<BaseChart option={chartOption} />
			</div>
		</div>
	);
}
